import { HelpBtn } from './HelpBtn';
import MaskToggle from './MaskToggle';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Trash2, Plus, Camera, Video, Shovel } from 'lucide-react';
import { computePit, getPitPrices, setPitPrice, autoArea, rectFromParams, pitCenter, DN_LIST, f2, SOIL_ANGLE, SHORE_DEPTH, slopeOf } from '../lib/excavation';
import { compressImage, uid } from '../lib/model';
import { videoThumb } from '../lib/media';
import { eur } from '../lib/license';
import PitLayerCard, { NumIn } from './PitLayerCard';
import MediaThumb from './MediaThumb';
import { VoiceRecorder, MemoList } from './VoiceMemo';

function Geometry({ t, pit, set }) {
  const setRect = (len, wid) => set({ len, wid, pts: rectFromParams(pit.center || pitCenter(pit), len, wid, pit.rot || 0) });
  return (
    <div className="pit-grid" data-testid="pit-geometry">
      {pit.shape === 'rect' && <NumIn label={t.pitLen} testid="pit-len-input" value={pit.len} onChange={(v) => v > 0 && setRect(v, pit.wid)} />}
      {pit.shape === 'rect' && <NumIn label={t.pitWid} testid="pit-wid-input" value={pit.wid} onChange={(v) => v > 0 && setRect(pit.len, v)} />}
      {pit.shape === 'circle' && <NumIn label={t.pitDia} testid="pit-dia-input" value={pit.r * 2} onChange={(v) => v > 0 && set({ r: v / 2 })} />}
      <NumIn label={t.pitArea} testid="pit-area-input" value={pit.areaOverride} auto={autoArea(pit)} onChange={(v) => set({ areaOverride: v })} />
      <NumIn label={t.pitDepth} testid="pit-depth-input" value={pit.depth} onChange={(v) => set({ depth: v ?? 0 })} />
      <label className="pit-num"><span>{t.pitDn}</span>
        <input list="pit-dn-list" type="number" inputMode="numeric" data-testid="pit-dn-input" value={pit.dn ?? ''} onChange={(e) => set({ dn: e.target.value === '' ? 0 : +e.target.value })} />
        <datalist id="pit-dn-list">{DN_LIST.map((d) => <option key={d} value={d} />)}</datalist>
      </label>
      <Shoring t={t} pit={pit} set={set} />
    </div>
  );
}

// DIN 4124: from 1.25 m depth slope (by soil) or shoring
function Shoring({ t, pit, set }) {
  const deep = +pit.depth > SHORE_DEPTH, sl = slopeOf(pit, +pit.areaOverride || autoArea(pit), +pit.depth || 0);
  return (
    <div className={`pit-shore ${deep && !pit.soil ? 'warn' : ''}`} data-testid="pit-shore">
      <label className="pit-num"><span>{t.pitSoil}</span>
        <select data-testid="pit-soil-select" value={pit.soil || ''} onChange={(e) => set({ soil: e.target.value })}>
          <option value="">{t.pitSoil_none}</option>
          {['nc', 'coh', 'rock'].map((k) => <option key={k} value={k}>{t['pitSoil_' + k]} ({SOIL_ANGLE[k]}°)</option>)}
          <option value="shore">{t.pitSoil_shore}</option>
        </select>
      </label>
      {deep && !pit.soil && <p className="small-hint bad" data-testid="pit-shore-warn">{t.pitShoreWarn}</p>}
      {deep && sl.extra > 0 && <p className="small-hint" data-testid="pit-slope-info">{t.pitSlopeInfo.replace('{a}', sl.angle).replace('{w}', sl.w.toFixed(2).replace('.', ',')).replace('{v}', sl.extra.toFixed(2).replace('.', ','))}</p>}
      {!deep && <p className="muted small-hint">{t.pitShoreOk}</p>}
    </div>
  );
}

function PipeZone({ t, pit, c, set }) {
  return (
    <div className="pit-grid" data-testid="pit-pipe-zone">
      <NumIn label={t.pitOd} testid="pit-od-input" value={pit.odOverride} auto={(pit.dn || 0) / 1000} step="0.001" onChange={(v) => set({ odOverride: v })} />
      <NumIn label={t.pitPipeLen} testid="pit-pipelen-input" value={pit.pipeLenOverride} auto={c.pipeLen} onChange={(v) => set({ pipeLenOverride: v })} />
      <NumIn label={t.pitBed} testid="pit-bed-input" value={pit.bed} scale={100} step="1" onChange={(v) => set({ bed: v ?? 0 })} />
      <NumIn label={t.pitCover} testid="pit-cover-input" value={pit.cover} scale={100} step="1" onChange={(v) => set({ cover: v ?? 0 })} />
    </div>
  );
}

function Media({ t, pit, set }) {
  const cam = useRef(null);
  const vid = useRef(null);
  const add = async (e, kind) => {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    const item = kind === 'video' ? { id: uid('ph'), kind: 'video', blob: file, mime: file.type, caption: '', ...(await videoThumb(file)) } : { id: uid('ph'), kind: 'photo', caption: '', ...(await compressImage(file)) };
    set((p) => ({ media: [...(p.media || []), item] }));
  };
  return (
    <div className="pit-media">
      <div className="row">
        <input type="text" data-testid="pit-note-input" placeholder={t.pitNote} value={pit.note || ''} onChange={(e) => set({ note: e.target.value })} />
        <button className="btn btn-outline btn-sm" data-testid="pit-photo-btn" onClick={() => cam.current.click()}><Camera size={15} /> {t.btnPhoto}</button>
        <button className="btn btn-outline btn-sm" data-testid="pit-video-btn" onClick={() => vid.current.click()}><Video size={15} /> {t.video}</button>
        <VoiceRecorder t={t} compact testid="pit-memo-btn" onDone={(m) => set((p) => ({ memos: [...(p.memos || []), m] }))} />
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden data-testid="pit-photo-input" onChange={(e) => add(e, 'photo')} />
        <input ref={vid} type="file" accept="video/*" capture="environment" hidden data-testid="pit-video-input" onChange={(e) => add(e, 'video')} />
      </div>
      {(pit.media || []).length > 0 && (
        <div className="fit-media">
          {pit.media.map((m, j) => (
            <div key={m.id} className="fit-media-item" data-testid={`pit-media-${j}`}>
              <MediaThumb item={m} className="fit-thumb" />
              <button className="icon-btn danger mini-del" data-testid={`pit-media-delete-${j}`} onClick={() => set((p) => ({ media: p.media.filter((y) => y.id !== m.id) }))}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <MemoList t={t} memos={pit.memos} prefix="pit" onDelete={(mid) => set((p) => ({ memos: p.memos.filter((y) => y.id !== mid) }))} />
    </div>
  );
}

export default function PitDialog({ t, pits }) {
  const [pit, setPit] = useState(pits.editing);
  const [prices, setPrices] = useState(getPitPrices());
  const set = (patch) => setPit((p) => ({ ...p, ...(typeof patch === 'function' ? patch(p) : patch) }));
  const c = computePit({ ...pit, prices: undefined }, prices);
  const qty = (key) => (v) => set((p) => ({ qty: { ...p.qty, [key]: v } }));
  const dens = (key) => (v) => set((p) => ({ dens: { ...p.dens, [key]: v } }));
  const price = (mat) => (patch) => {
    if (prices[mat]?.proj) pits.setProjPitPrice(mat, patch); else setPitPrice(mat, patch);
    setPrices(getPitPrices());
  };
  const custom = (id, patch) => set((p) => ({ custom: p.custom.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const thickFor = (r) => {
    const tid = `pit-layer-${r.key}-thick`;
    if (r.key === 'sand') return <NumIn label={t.pitColThick} testid={tid} value={pit.sandOverride} auto={r.auto} scale={100} step="1" onChange={(v) => set({ sandOverride: v })} />;
    if (r.key === 'fill') return <NumIn label={t.pitColThick} testid={tid} value={pit.fillOverride} auto={r.auto} scale={100} step="1" onChange={(v) => set({ fillOverride: v })} />;
    if (r.key === 'surface') return <NumIn label={t.pitColThick} testid={tid} value={pit.surfThick} scale={100} step="1" onChange={(v) => set({ surfThick: v ?? 0 })} />;
    return <NumIn label={t.pitColThick} testid={tid} value={r.thick} scale={100} step="1" onChange={(v) => custom(r.key, { thick: v ?? 0 })} />;
  };
  const card = (r) => {
    const isCustom = r.mat === 'custom';
    return (
      <PitLayerCard key={r.key} t={t} row={r} thick={r.key === 'excav' ? null : thickFor(r)} qtyVal={pit.qty?.[r.key]} onQty={qty(r.key)}
        onDens={isCustom ? (v) => custom(r.key, { density: v }) : dens(r.mat)} onPrice={isCustom ? (p) => custom(r.key, p) : price(r.mat)}
        onRemove={isCustom ? () => set((p) => ({ custom: p.custom.filter((x) => x.id !== r.key) })) : null}
        onName={isCustom ? (name) => custom(r.key, { name }) : null}
        extra={r.key === 'surface' ? <SurfaceSel t={t} value={pit.surface} onChange={(surface) => set({ surface })} /> : null} />
    );
  };
  const rows = [...c.rows].reverse();

  return createPortal(
    <div className="editor-backdrop" data-testid="pit-dialog">
      <div className="editor pit-dialog mask">
        <div className="editor-head mask-keep">
          <h4><Shovel size={18} /> {t.pitTitle}</h4>
          <MaskToggle t={t} id="pit-dialog" /><HelpBtn k="n:meas_pit" testid="pit-dialog-help" title={t.helpHere} /><button className="icon-btn" data-testid="pit-dialog-close-btn" onClick={() => pits.setEditing(null)}><X size={16} /></button>
        </div>
        <input className="pit-name" data-testid="pit-name-input" value={pit.name} onChange={(e) => set({ name: e.target.value })} />
        <div className="pit-kpis" data-testid="pit-kpis">
          <span><b data-testid="pit-kpi-area">{f2(c.area, t.locale)}</b> m²</span>
          <span><b data-testid="pit-kpi-excav">{f2(c.excav.m3, t.locale)}</b> m³ {t.pitExcav}</span>
          <span><b data-testid="pit-kpi-total">{eur(c.total, t.locale)}</b> {t.pitTotal}</span>
        </div>
        <Geometry t={t} pit={pit} set={set} />
        <PipeZone t={t} pit={pit} c={c} set={set} />
        <p className="muted small-hint">{t.pitColLayer} · {t.pitPipeDisp}: {f2(c.pipeVol, t.locale)} m³</p>
        <div className="pit-layers">
          {(pit.surface === 'none') && <div className="pit-layer"><div className="pit-layer-head"><b>{t.pitSurface}</b><SurfaceSel t={t} value={pit.surface} onChange={(surface) => set({ surface })} /></div></div>}
          {rows.map(card)}
          <button className="btn btn-outline btn-sm" data-testid="pit-add-layer-btn" onClick={() => set((p) => ({ custom: [...(p.custom || []), { id: uid('lay'), name: '', thick: 0.1, density: 1.8, price: null, unit: 'm3' }] }))}><Plus size={14} /> {t.pitAddLayer}</button>
          {card(c.excav)}
        </div>
        <Media t={t} pit={pit} set={set} />
        <div className="row pit-actions">
          <button className="btn btn-green" data-testid="pit-save-btn" onClick={() => pits.save({ ...pit, prices })}><Save size={16} /> {t.pitSave}</button>
          {!pit.isNew && <button className="btn btn-outline" data-testid="pit-delete-btn" onClick={() => pits.remove(pit.id)}><Trash2 size={16} /> {t.pitDelete}</button>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SurfaceSel({ t, value, onChange }) {
  return (
    <select data-testid="pit-surface-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="asphalt">{t.pitM_asphalt}</option>
      <option value="topsoil">{t.pitM_topsoil}</option>
      <option value="none">{t.pitSurf_none}</option>
    </select>
  );
}
